// Shared invoice HTML builder, extracted from DashboardDetailed.jsx's
// downloadInvoice so the multi-booking download flow there and the
// single-booking "Generate Invoice" button (Bookings.jsx) render an
// identical-looking invoice instead of two copies that drift apart.
//
// Callers normalize their own raw API shape into the `bookings` array shape
// documented on buildInvoiceHtml below (DashboardDetailed's list rows use
// item_names/item_prices comma strings; Bookings.jsx's get_ezp_booking_by_id
// uses a "name::price||name2::price2" custom_items string — both get
// converted to the same addedItems: [{name, price}] shape by their caller,
// not by this file, so this file stays agnostic of either backend's raw
// column names).

import { isSettledPayment } from "./paymentStatus";

export const formatDateDisplay = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

export const formatAmount = (val) => {
  if (val === null || val === undefined || val === "" || val === "undefined") {
    return "0.00";
  }
  return Number(val).toFixed(2);
};

export const convertTo12Hour = (time24) => {
  if (!time24) return "";
  const [hourStr, minute] = time24.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${ampm}`;
};

const safe = (val) => {
  if (val === null || val === undefined || val === "undefined") return "";
  return val;
};

const sumField = (bookings, field) =>
  (bookings || []).reduce((s, ele) => s + (parseFloat(ele?.[field]) || 0), 0);

// `safe` above only null-guards. Asset values are free text typed by users,
// so the asset markup is escaped.
const escapeHtml = (val) =>
  String(safe(val))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Asset the booking was done for (vehicle, property/appliance, ...), shown
// between a booking's header and its line-items table, e.g.
//   Vehicle
//   Make / Brand: Honda   Model: City   Model Year: 2026   Vehicle Type: Sedan
// Returns "" when the booking has no asset, so such invoices render as before.
const buildAssetBlock = (asset) => {
  if (!asset || (!asset.title && !(asset.fields || []).length)) return "";
  const fields = (asset.fields || [])
    .filter((f) => f && f.value)
    .map(
      (f) =>
        `<span style="margin-right:1.25rem; white-space:nowrap;"><span style="color:#4a5568;">${escapeHtml(f.label)}:</span> <strong>${escapeHtml(f.value)}</strong></span>`
    )
    .join("");
  return `
    <div style="margin-bottom:6px; padding:0.45rem 0.75rem; background-color:#F9FAFB; border:1px solid #E5E7EB; border-radius:4px; font-size:0.8rem;">
      <strong>${escapeHtml(asset.type_label || "Asset")}${asset.title ? `: ${escapeHtml(asset.title)}` : ""}</strong>
      ${fields ? `<div style="margin-top:2px;">${fields}</div>` : ""}
    </div>`;
};

const round2 = (n) => Math.round(n * 100) / 100;

// get_ezp_booking_by_id's group id is the last "_" segment of the display
// booking id (e.g. "2026_10_ACRM_50" -> "50"), same as Bookings.jsx's route.
export const bookingGroupId = (row) =>
  row?.group_id ||
  String(row?.display_booking_id || "").split("_").pop() ||
  null;

/**
 * Turns one booking summary row (whose service/type/offering names may be
 * comma-joined across several offerings, with a single combined total/tax)
 * into one invoice line per offering, using the per-offering rows from
 * get_ezp_booking_by_id (each has service_name, service_type_name,
 * service_offering_name, price).
 *
 * Each line carries its offering's own booked `price` (the Rate shown on the
 * invoice) and `line_discount` (that offering's own discount). The
 * booking-level money - total, tax, discount, paid amount,
 * added items - stays on the first line only, so summing the lines still
 * gives the booking's figures and nothing is counted once per offering.
 * buildInvoiceHtml works out each line's tax from these. All lines keep the
 * summary's booking_id, so parts and booking_ids aren't duplicated.
 */
export const splitIntoOfferings = (summary, offeringRows) => {
  const seen = new Set();
  const offerings = (offeringRows || []).filter((r) => {
    const key = String(r?.id ?? r?.service_offering_id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (offerings.length === 0) return [summary];
  if (offerings.length === 1) {
    return [
      {
        ...summary,
        price: offerings[0].price,
        line_discount: offerings[0].line_discount
      }
    ];
  }

  return offerings.map((o, idx) => ({
    ...summary,
    service_name: o.service_name,
    service_type_name: o.service_type_name,
    service_offering_name: o.service_offering_name,
    price: o.price,
    line_discount: o.line_discount,
    total: idx === 0 ? summary.total : 0,
    tax: idx === 0 ? summary.tax : 0,
    discount: idx === 0 ? summary.discount : 0,
    total_paid_amount: idx === 0 ? summary.total_paid_amount : 0,
    addedItems: idx === 0 ? summary.addedItems : []
  }));
};

/**
 * @param {Object} args
 * @param {Array} args.bookings - normalized booking rows, each:
 *   { booking_id, group_id, display_booking_id, org_id, service_name,
 *     service_type_name, service_offering_name, service_date, service_time,
 *     total, tax, discount, total_paid_amount, commission,
 *     partner_name, user_name, house_no, area, landmark, location, state,
 *     phone_number,
 *     addedItems: [{ name, price }] }
 *   Rows sharing a group_id (or display_booking_id) are one booking with
 *   several offerings: rendered as one section, one line per offering.
 * @param {Object} args.partsByBookingId - { [booking_id]: partsArray } from
 *   inventoryService.getBookingParts (bp.* joined to i.name/i.sku)
 * @param {Object} args.assetsByBookingId - optional, { [booking_id]: asset }
 *   from customerAssetService.getInvoiceAssets. A booking with no entry
 *   renders without an asset block.
 * @param {Array} args.vendorData - [{ profile_image, first_name, last_name,
 *   vendor_address, tax_no }] from get_ezp_vendor_data
 * @param {string} args.currency
 * @param {boolean} args.isClinicService
 * @returns {{ htmlContent: string, booking_ids: Array }}
 */
export function buildInvoiceHtml({
  bookings,
  partsByBookingId = {},
  assetsByBookingId = {},
  vendorData,
  currency,
  isClinicService = false,
  invoiceDate: chosenInvoiceDate = null
}) {
  const sortedData = [...(bookings || [])].sort(
    (a, b) => new Date(a.service_date) - new Date(b.service_date)
  );
  const getAmount = (label) => sumField(sortedData, label);
  const commissionPercent = Number(sortedData?.[0]?.commission || 0);
  const hasOriginId = sortedData?.some(
    (ele) => ele?.org_id && ele?.org_id !== ""
  );

  let htmlContent = `
      <!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Invoice Download</title>
    <style>
      body {
        font-family: "Poppins";
        background-color: #f9f9f9;
        margin: 0;
        padding: 0 2rem;
        font-size: 0.8rem;
      }
      .d-flex {
        display: flex;
      }
      .flex-center {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .justify-content-end {
        justify-content: flex-end;
      }

      .justify-between {
        display: flex;
        justify-content: space-between;
      }
      .invoice {
        margin-right: 2rem;
        font-size: 1rem;
        font-weight: 600;
      }
       .invoice_date {
        margin-right: 2rem;
        font-size: 0.8rem;
        font-weight: 400;
        margin-top:"8px"
      }

      .text-center {
        text-align: center;
      }
      .fs2rem {
        font-size: 2rem;
      }
      .w15 {
        width: 15%;
      }
      .w-33 {
        width: 33%;
      }
      .w-50 {
        width: 50%;
      }
      .logoTitle {
        font-size: 1.5rem;
        font-weight: 600;
        color: #fff;
        background: #c9194a;
        padding: 1rem;
      }
      .gap-4 {
        gap: 8rem;
      }
      .text-gray-600 {
        color: #4a5568;
        font-size: 1.25rem;
      }
      .m-2 {
        margin: 1rem 0rem 0.2rem 0;
      }
      .mt-halfrem {
        margin-top: 0.5rem;
      }
      .bold {
        font-weight: 600;
      }
      .flex-start {
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
      table {
        width: 100%;
        margin-top: 1rem;
        border-collapse: collapse;
      }

      table tr {
        border-bottom: 1px solid #ccc;
      }

      th,
      td {
        padding: 0.6rem 0.75rem;
        text-align: left;
        font-size: 0.8rem;
      }

      .fs_normal{
        font-size: 0.8rem;
      }

      .leftRounded {
        border-top-left-radius: 0.375rem;
        border-bottom-left-radius: 0.375rem;
        padding: 0.5rem;
        background: #c9194a;
        color: #fff;
      }
    </style>
  </head>
  <body>
    <div>
      <div class="flex-center">
        <h3 class="fs2rem">Tax Invoice</h3>
      </div>
      <div>
`;

  // 'YYYY-MM-DD' picked in the Generate Invoice popup; today when not given.
  const invoiceDate = (chosenInvoiceDate ? new Date(`${chosenInvoiceDate}T00:00:00`) : new Date()).toLocaleDateString("en-GB");
  // Falls back to whichever of first/last name is present if the other is
  // missing, instead of leaving a trailing/leading blank space. Goes through
  // safe() on both parts because a missing name can come back as the literal
  // string "undefined" rather than a real null/undefined (see safe() above).
  const vendorFullName = [
    safe(vendorData?.[0]?.first_name),
    safe(vendorData?.[0]?.last_name)
  ]
    .filter(Boolean)
    .join(" ");
  // If the vendor hasn't filled in the free-text Address field, fall back to
  // city/state/country on one line (comma-separated, not stacked like before).
  const vendorAddressFallback = [
    safe(vendorData?.[0]?.city),
    safe(vendorData?.[0]?.state),
    safe(vendorData?.[0]?.country_name)
  ]
    .filter(Boolean)
    .join(", ");
  const vendorAddressDisplay =
    safe(vendorData?.[0]?.address) || vendorAddressFallback;

  console.log("vendorAddressDisplay---", vendorAddressDisplay);
  console.log("vendorData---", vendorData);
  console.log("vendorAddressFallback---", vendorAddressFallback);

  if (vendorData?.[0]?.profile_image) {
    htmlContent += `
    <div class="d-flex justify-between" style="align-items: flex-start; margin-bottom: 10px;">
      <!-- LEFT: Logo + Company Info -->
      <div style="display: flex; flex-direction: row; align-items: flex-start; gap: 12px;">
        <img
          src="${vendorData[0].profile_image}"
          alt="profile"
          style="width:90px; height:90px; object-fit:contain; flex-shrink:0;"
        />
        <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 2px;">
          ${vendorFullName ? `<div>${vendorFullName}</div>` : ""}
           ${
             vendorAddressDisplay.includes("~")
               ? `
      ${vendorAddressDisplay
        .split("~")
        .map(
          (line) =>
            `<div class="text-gray-600 fs_normal">${line
              .trim()
              .replace(/^,\s*/, "")
              .replace(/,\s*$/, "")}</div>`
        )
        .join("")}
    `
               : `<div class="text-gray-600 fs_normal">${vendorAddressDisplay}</div>`
           }
          ${vendorData?.[0]?.tax_no ? `<div><strong>TRN:</strong> ${vendorData[0].tax_no}</div>` : ""}
        </div>
      </div>

      <!-- RIGHT: Invoice Info -->
      <div style="display: flex; flex-direction: column; align-items: flex-end;">
        <div class="invoice">INVOICE</div>
        <div class="invoice_date">Invoice Date: ${invoiceDate}</div>
      </div>
    </div>
  `;
  } else {
    // No profile_image uploaded — no logo placeholder frame at all, just
    // the plain company details (no dashed box standing in for a logo).
    htmlContent += `
    <div class="d-flex justify-between" style="align-items: flex-start; margin-bottom: 10px;">
      <!-- LEFT: Company Details -->
      <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 2px;">
        ${vendorFullName ? `<div style="font-weight:600; font-size:1.1rem;">${vendorFullName}</div>` : ""}
        ${
          vendorAddressDisplay.includes("~")
            ? `
      ${vendorAddressDisplay
        .split("~")
        .map(
          (line) =>
            `<div class="text-gray-600 fs_normal">${line
              .trim()
              .replace(/^,\s*/, "")
              .replace(/,\s*$/, "")}</div>`
        )
        .join("")}
    `
            : `<div class="text-gray-600 fs_normal">${vendorAddressDisplay}</div>`
        }
        ${vendorData?.[0]?.tax_no ? `<div><strong>TRN:</strong> ${vendorData[0].tax_no}</div>` : ""}
      </div>

      <!-- RIGHT: Invoice Info -->
      <div style="display: flex; flex-direction: column; align-items: flex-end;">
        <div class="invoice">INVOICE</div>
        <div class="invoice_date">Invoice Date: ${invoiceDate}</div>
      </div>
    </div>
  `;
  }

  let bill_to = sortedData[0]?.partner_name;
  let addressBlock = "";

  if (sortedData[0]?.partner_name === "Self") {
    bill_to = sortedData[0]?.user_name;
  }

  if (
    sortedData[0]?.partner_name &&
    sortedData[0]?.partner_name.toLowerCase() === "utilstop"
  ) {
    addressBlock = `
    <div class="text-gray-600 fs_normal">
      38 Tanvi Business Center, Horizon Building, Port Saeed,
      Opposite DIB, Dubai, U.A.E
    </div>
  `;
  } else {
    addressBlock = `
    ${sortedData[0]?.house_no ? `<div class="text-gray-600 fs_normal">${sortedData[0].house_no}</div>` : ""}
    ${sortedData[0]?.area ? `<div class="text-gray-600 fs_normal">${sortedData[0].area}</div>` : ""}
    ${sortedData[0]?.landmark ? `<div class="text-gray-600 fs_normal">${sortedData[0].landmark}</div>` : ""}
    ${sortedData[0]?.location ? `<div class="text-gray-600 fs_normal">${sortedData[0].location}</div>` : ""}
    ${sortedData[0]?.state ? `<div class="text-gray-600 fs_normal">${sortedData[0].state}</div>` : ""}
    ${safe(sortedData[0]?.phone_number) ? `<div class="text-gray-600 fs_normal">Phone: ${sortedData[0].phone_number}</div>` : ""}
  `;
  }

  htmlContent += `
<hr style="border: none; border-top: 2px solid #c9194a; margin: 8px 0 16px;" />
<div style="margin-bottom: 10px;">
  <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 2px;">

    <div>
      <strong>Bill to : ${bill_to ? bill_to : "N/A"}</strong>
    </div>

    ${addressBlock}

  </div>


  <div style="margin-top: 5px; font-size: 15px; color: black; text-align: right;">
    * All values in ${currency}
  </div>
</div>
`;

  let rows = [];
  let booking_ids = [];
  // Sum of every booking's saved total (which already includes its parts and
  // added items), accumulated per-booking as bookingSubtotal in the loop.
  let grandTotal = 0;
  let paidTotal = 0;
  // Every offering's own discount; the bottom Discount is these plus the
  // bookings' overall discounts.
  let lineDiscountTotal = 0;
  // #, [Origin ID], Items & Description, Rate, Discount, Amount.
  const mainColSpan = hasOriginId ? 6 : 5;
  // Parts/Added Items rows use fewer <td>s than the main row, so without
  // colspan their cells don't land under the same columns as the main row's
  // (browsers line up cells by position, not by matching header text) -
  // that's what was leaving Amount short of the real right edge. colspan
  // pads Name/Item out to fill the leading columns (also giving it more
  // room, as asked) so the last cell (Amount/Cost) is always the table's
  // true last column, exactly under the main row's Amount.
  const partsNameColSpan = mainColSpan - 3; // Name, then SKU, Qty, Amount (3 more cells)
  const addedItemNameColSpan = mainColSpan - 1; // Item, then Cost (1 more cell)

  // A booking with several offerings comes back as one row per offering
  // (same group_id / display_booking_id, each with its own price/tax/total).
  // Group them so each booking renders as one section with a separate line
  // per offering, instead of one section per row (or only the first row).
  const groupKey = (ele) =>
    ele?.group_id || ele?.display_booking_id || ele?.booking_id;
  const groups = [];
  const groupsByKey = new Map();
  sortedData.forEach((ele) => {
    const key = groupKey(ele);
    if (!groupsByKey.has(key)) {
      const g = [];
      groupsByKey.set(key, g);
      groups.push(g);
    }
    groupsByKey.get(key).push(ele);
  });

  // line: { rate, discount, amount } worked out per group below.
  const offeringRow = (ele, idx, line) => {
    const paidAmount = Number(ele.total_paid_amount || 0);
    return `
    <tr>
      <td>${idx + 1}</td>
      ${hasOriginId ? `<td>${safe(ele["org_id"] || "")}</td>` : ""}

      <td>
        ${safe(ele["service_name"])}

        ${ele["service_type_name"] ? ` - ${safe(ele["service_type_name"])}` : ""}

        ${ele["service_offering_name"] ? ` - ${safe(ele["service_offering_name"])}` : ""}

        ${
          paidAmount > 0
            ? `<br/>
               <span style="color: green; font-size: 12px;">
                 (Paid: ${currency} ${formatAmount(paidAmount)})
               </span>`
            : ``
        }
      </td>

      <td style="text-align:right;">${formatAmount(line.rate)}</td>
      <td style="text-align:right;">${formatAmount(line.discount)}</td>
      <td style="text-align:right;">${formatAmount(line.amount)}</td>
    </tr>`;
  };

  groups.forEach((group) => {
    const ele = group[0];
    // Rows split out of one booking by splitIntoOfferings share a
    // booking_id - push/look up parts once per real booking, not per line.
    const groupBookingIds = [...new Set(group.map((g) => g?.booking_id))];
    booking_ids.push(...groupBookingIds);

    // Parts Used - plain <tr> rows appended directly into this same table's
    // <tbody> (not a nested <table>). Header row is plain... styled with the
    // light-pink band, matching Added Items below. Only line_total is shown
    // (no unit_price column) - line_total already comes straight from
    // ezp_booking_parts.line_total (computed/stored server-side), never
    // recomputed here.
    const parts = groupBookingIds.flatMap((id) => partsByBookingId[id] || []);
    const partsSum = parts.reduce((s, p) => s + (Number(p.line_total) || 0), 0);
    const partRows =
      parts.length > 0
        ? `
    <tr style="font-weight:600; background-color:#fff1f4; color:#c9194a;">
      <td colspan="${partsNameColSpan}" style="font-size:12px;">Inventory Name</td>
      <td style="font-size:12px;">SKU</td>
      <td style="font-size:12px; text-align:right;">Qty</td>
      <td style="font-size:12px; text-align:right;">Amount</td>
    </tr>
    ${parts
      .map(
        (p) => `
    <tr>
      <td colspan="${partsNameColSpan}">${safe(p.item_name)}</td>
      <td>${safe(p.sku || "-")}</td>
      <td style="text-align:right;">${Number(p.quantity) || 0}</td>
      <td style="text-align:right;">${formatAmount(p.line_total || 0)}</td>
    </tr>
  `
      )
      .join("")}
  `
        : "";

    // Added Items - same plain-<tr>-in-this-tbody approach, using the
    // caller-normalized addedItems: [{name, price}] array.
    const addedItems = group.flatMap((g) => g.addedItems || []);
    const addedItemsSum = addedItems.reduce(
      (s, item) => s + (Number(item.price) || 0),
      0
    );
    const addedItemRows =
      addedItems.length > 0
        ? `
    <tr style="font-weight:600; background-color:#fff1f4; color:#c9194a;">
      <td colspan="${addedItemNameColSpan}" style="font-size:12px;">Item</td>
      <td style="font-size:12px; text-align:right;">Cost</td>
    </tr>
    ${addedItems
      .map(
        (item) => `
    <tr>
      <td colspan="${addedItemNameColSpan}">${safe(item.name)}</td>
      <td style="text-align:right;">${formatAmount(item.price)}</td>
    </tr>
  `
      )
      .join("")}
  `
        : "";

    // The saved booking total already includes the added items and parts
    // (plus tax on them, minus the discount) - it's the booking screen's
    // Total - so it is the booking subtotal as-is; adding parts/items again
    // would count them twice.
    const bookingSubtotal = sumField(group, "total");
    const bookingTax = sumField(group, "tax");
    const bookingDiscount = sumField(group, "discount");
    grandTotal += bookingSubtotal;

    // Service lines (before tax): Rate is the offering's booked price,
    // Discount its own line_discount. The overall discount (`discount`) gets
    // one row below the items; tax is left to the invoice totals, so the
    // rows add up to the booking subtotal shown (before tax). Rows
    // without a price (list rows not split by splitIntoOfferings) share
    // what's left of the subtotal once parts and added items are taken out.
    const preTaxSubtotal = bookingSubtotal - bookingTax + bookingDiscount;
    const servicesBase = preTaxSubtotal - partsSum - addedItemsSum;
    const hasPrices = group.every(
      (g) => g.price !== undefined && g.price !== null && g.price !== ""
    );
    const offeringLines = group.map((g) => {
      const discount = hasPrices ? Number(g.line_discount) || 0 : 0;
      const rate = hasPrices
        ? Number(g.price) || 0
        : round2(servicesBase / group.length);
      return { rate, discount, amount: round2(rate - discount) };
    });
    lineDiscountTotal += offeringLines.reduce((s, l) => s + l.discount, 0);
    const overallDiscountRow =
      bookingDiscount >= 0.01
        ? `
    <tr>
      <td colspan="${addedItemNameColSpan}">Discount</td>
      <td style="text-align:right;">-${formatAmount(bookingDiscount)}</td>
    </tr>`
        : "";
    // Shown per booking before tax; the Tax / Total at the bottom add it.
    const bookingSubtotalBeforeTax = bookingSubtotal - bookingTax;
    // A settled payment status (Cash paid / Paid Online / Bank Transfer) means
    // the whole booking was paid, even if paid_amount was never filled in.
    // get_ezp_booking_by_id calls the status id `payment`, the get_ezp_booking
    // list (dashboard invoices) calls it `payment_int`.
    paidTotal += isSettledPayment(ele.payment ?? ele.payment_int)
      ? bookingSubtotal * (1 - commissionPercent / 100)
      : sumField(group, "total_paid_amount");
    const subtotalColspan = mainColSpan - 1;

    rows.push(`
  <section style="margin-bottom: 1.25rem; border: 1px solid #E5E7EB; border-radius: 8px; padding: 0.75rem 1rem;">
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; background-color:#fff1f4; padding:0.5rem 0; border-radius:4px;">
      <div style="font-weight:600; color:#c9194a;">Booking ${safe(ele["display_booking_id"])}</div>
      <div style="color:#c9194a; font-size:13px;">
        ${ele["service_date"] ? formatDateDisplay(ele["service_date"]) : ""}${
          ele["service_time"]
            ? ` &middot; ${convertTo12Hour(ele["service_time"])}`
            : ""
        }
      </div>
    </div>${groupBookingIds
      .map((id) => buildAssetBlock(assetsByBookingId?.[id]))
      .find(Boolean) || ""}
    <table style="margin-top: 0;">
      <thead>
        <tr>
          <th>#</th>
          ${hasOriginId ? `<th>Origin ID</th>` : ""}
          ${isClinicService ? `<th>Specialization & Description</th>` : `<th>Items & Description </th>`}
          <th style="text-align:right;">Rate</th>
          <th style="text-align:right;">Discount</th>
          <th style="text-align:right;">Amount</th>
        </tr>
      </thead>
      <tbody>
    ${group.map((g, idx) => offeringRow(g, idx, offeringLines[idx])).join("")}
    ${partRows}
    ${addedItemRows}
    ${overallDiscountRow}
    <tr style="background-color:#F3F4F6; font-weight:600;">
      <td colspan="${subtotalColspan}" style="text-align:right; white-space:nowrap;">Booking subtotal:</td>
      <td style="text-align:right; white-space:nowrap;">${currency} ${formatAmount(bookingSubtotalBeforeTax)}</td>
    </tr>
      </tbody>
    </table>
  </section>
  `);
  });

  htmlContent += rows.join("");

  // Totals from grandTotal (every booking's saved total, accumulated above).
  const correctCommissionAmount = (grandTotal * commissionPercent) / 100;
  // Net of commission - "Total" below now shows grandTotal minus commission,
  // so Balance Due (Total minus what's already been paid) has to match that
  // same net figure, not the gross grandTotal.
  const netTotal = grandTotal - correctCommissionAmount;
  const balanceDue = netTotal - paidTotal;

  htmlContent += `
<section style="margin-top: 4rem;">

  <div style="display:flex; width:100%; gap:20px; align-items:flex-start;">

    <!-- RIGHT SIDE (ALWAYS SHOW) -->
    <div style="width:30%; margin-left:auto;">

      <div class="d-flex mt-halfrem">
        <div class="w-50 bold">Discount</div>
        <div class="w-50 bold" style="text-align:right;">${currency} ${formatAmount(getAmount("discount") + lineDiscountTotal)}</div>
      </div>

      <div class="d-flex mt-halfrem">
        <div class="w-50 bold">Tax</div>
        <div class="w-50 bold" style="text-align:right;">${currency} ${formatAmount(getAmount("tax"))}</div>
      </div>

${
  commissionPercent > 0
    ? `
  <div class="d-flex mt-halfrem">
    <div class="w-50 bold">
      Commission (${commissionPercent}%)
    </div>
    <div class="w-50 bold" style="text-align:right;">
      -${currency} ${formatAmount(correctCommissionAmount)}
    </div>
  </div>
`
    : ``
}
      <div class="d-flex mt-halfrem leftRounded">
        <div class="w-50 bold">Total</div>
        <div class="w-50 bold" style="text-align:right;">${currency} ${formatAmount(netTotal)}</div>
      </div>
      <div class="d-flex mt-halfrem">
        <div class="w-50 bold">Total Paid Amount</div>
<div class="w-50 bold" style="text-align:right;">
  ${currency} ${formatAmount(paidTotal)}
</div>      </div>

      <div class="d-flex mt-halfrem leftRounded">
        <div class="w-50 bold">Balance Due</div>
        <div class="w-50 bold" style="text-align:right;">${currency} ${formatAmount(balanceDue)}</div>
      </div>

    </div>

  </div>

</section>
`;

  htmlContent += `  <div style="margin-top: 4rem"></div>

      <div class="footer-note text-center text-gray-400 text-sm border-t pt-4">
        <p class="font-semibold mb-1">Thank you for your business.</p>
        <p>
          This is a system generated invoice, no signature required for the
          same.
        </p>
      </div>
    </div>
  </body>
</html>`;

  return { htmlContent, booking_ids };
}
